// src/components/tiles/JobMapBoard.jsx
import React from 'react';
import { Box, Typography, Card, CardContent, Stack, Dialog, DialogTitle, DialogContent, Divider } from '@mui/material';
import ScoreBadge from './ScoreBadge';
import ReceiptsList from './ReceiptsList';

export default function JobMapBoard({ columns = [] }) {
  const [open, setOpen] = React.useState(false);
  const [modalStep, setModalStep] = React.useState(null);

  const onOpen = (step) => { setModalStep(step); setOpen(true); };
  const onClose = () => { setOpen(false); setModalStep(null); };

  return (
    <>
      <Box
        sx={{
          overflowX: 'auto',
          overflowY: 'hidden',
          pb: 1,
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1,
          bgcolor: 'background.paper'
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'nowrap',          // no wrapping; use scroll
            gap: 2,
            px: 2,
            py: 2,
            minHeight: 180
          }}
        >
          {columns.map((col, ci) => (
            <Box key={ci} sx={{ flex: '0 0 auto', minWidth: 360 }}>
              {/* Stage title already numbered by selector (e.g., "1. Define") */}
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700 }}>
                {col.stage}
              </Typography>

              {/* Steps in a row inside this column */}
              <Box sx={{ display: 'flex', flexWrap: 'nowrap', gap: 1 }}>
                {(col.steps || []).map((step, si) => (
                  <Card
                    key={si}
                    sx={{ flex: '0 0 320px', cursor: 'pointer' }}
                    onClick={() => onOpen(step)}
                  >
                    <CardContent>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                        {step.name}
                      </Typography>

                      {step.description && (
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                          <strong>How they do it:</strong> {step.description}
                        </Typography>
                      )}

                      <Stack direction="row" spacing={1} sx={{ mb: 1, flexWrap: 'wrap' }}>
                        {Number.isFinite(step.satisfaction_score) && (
                          <ScoreBadge
                            label="Satisfaction"
                            value={step.satisfaction_score}
                            metricKey="satisfaction"
                          />
                        )}
                      </Stack>

                      <ReceiptsList receipts={step.receipts || []} />
                    </CardContent>
                  </Card>
                ))}
              </Box>
            </Box>
          ))}
        </Box>
      </Box>

      {/* Modal with full product_workflow_steps for the clicked step */}
      <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
        <DialogTitle>{modalStep?.name || 'Workflow step'}</DialogTitle>
        <DialogContent dividers>
          {modalStep?.description && (
            <>
              <Typography variant="body2" sx={{ mb: 1.5 }}>
                <strong>How they do it:</strong> {modalStep.description}
              </Typography>
              <Divider sx={{ mb: 2 }} />
            </>
          )}

          <Typography variant="subtitle2" sx={{ mb: 1 }}>Product workflow steps</Typography>
          <Box sx={{ display:'flex', flexDirection:'column', gap: 1.5 }}>
            {(modalStep?.product_workflow_steps || []).map((pwf, i) => (
              <Box key={i} sx={{ p: 1, border:'1px solid', borderColor:'divider', borderRadius: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                  {pwf.name || pwf.step_name || '—'}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                  {pwf.description || '—'}
                </Typography>
                <Stack direction="row" spacing={1} sx={{ mb: 0.5, flexWrap: 'wrap' }}>
                  {Number.isFinite(pwf.satisfaction_score) && (
                    <ScoreBadge label="Satisfaction" value={pwf.satisfaction_score} metricKey="satisfaction" />
                  )}
                  {pwf.product_name && (
                    <ScoreBadge label="Product" value={pwf.product_name} metricKey="text" />
                  )}
                </Stack>
                <ReceiptsList receipts={Array.isArray(pwf.receipts) ? pwf.receipts : []} />
              </Box>
            ))}
          </Box>
        </DialogContent>
      </Dialog>
    </>
  );
}
